/**
 * Editor для doc.currency_purchase_sale (Фаза 2.1 #2).
 * Live FX preview: обчислення на льоту при вводі курсу.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  currencyOperations,
  type CurrencyOperationType,
} from '@/api/currencyOperations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const PERMISSION = 'documents.currency_operations.create';

interface FormState {
  organization_id: string;
  document_date: string;
  operation_type: CurrencyOperationType;
  from_bank_account_id: string;
  to_bank_account_id: string;
  currency_id: string;
  currency_amount: string;
  commercial_rate: string;
  nbu_rate: string;
  bank_commission: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  operation_type: 'PURCHASE',
  from_bank_account_id: '',
  to_bank_account_id: '',
  currency_id: '',
  currency_amount: '0',
  commercial_rate: '0',
  nbu_rate: '0',
  bank_commission: '0',
  comment: '',
};

export default function CurrencyOperationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['currency-operation', id],
    queryFn: () => currencyOperations.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        operation_type: d.operation_type,
        from_bank_account_id: d.from_bank_account_id,
        to_bank_account_id: d.to_bank_account_id,
        currency_id: d.currency_id,
        currency_amount: d.currency_amount,
        commercial_rate: d.commercial_rate,
        nbu_rate: d.nbu_rate,
        bank_commission: d.bank_commission,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  // Live FX preview.
  const fxPreview = useMemo(() => {
    const amt = parseFloat(form.currency_amount) || 0;
    const cRate = parseFloat(form.commercial_rate) || 0;
    const nRate = parseFloat(form.nbu_rate) || 0;
    const uahC = amt * cRate;
    const uahN = amt * nRate;
    const diff = form.operation_type === 'PURCHASE' ? uahC - uahN : uahN - uahC;
    // Warning: commercial rate outside ±10% від NBU.
    let warn: string | null = null;
    if (nRate > 0 && cRate > 0) {
      if (cRate < nRate * 0.95) warn = 'Комерційний курс підозріло нижче за курс НБУ';
      else if (cRate > nRate * 1.10) warn = 'Комерційний курс підозріло вище за курс НБУ';
    }
    return { uahC, uahN, diff, warn };
  }, [form.currency_amount, form.commercial_rate, form.nbu_rate, form.operation_type]);

  const create = useMutation({
    mutationFn: () =>
      currencyOperations.create({
        organization_id: form.organization_id,
        document_date: new Date(form.document_date).toISOString(),
        operation_type: form.operation_type,
        from_bank_account_id: form.from_bank_account_id,
        to_bank_account_id: form.to_bank_account_id,
        currency_id: form.currency_id,
        currency_amount: form.currency_amount,
        commercial_rate: form.commercial_rate,
        nbu_rate: form.nbu_rate,
        bank_commission: form.bank_commission,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['currency-operations'] });
      navigate(`/banking/currency-operations/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await currencyOperations.create({
        organization_id: form.organization_id,
        document_date: new Date(form.document_date).toISOString(),
        operation_type: form.operation_type,
        from_bank_account_id: form.from_bank_account_id,
        to_bank_account_id: form.to_bank_account_id,
        currency_id: form.currency_id,
        currency_amount: form.currency_amount,
        commercial_rate: form.commercial_rate,
        nbu_rate: form.nbu_rate,
        bank_commission: form.bank_commission,
        comment: form.comment || null,
      });
      await currencyOperations.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['currency-operations'] });
      navigate(`/banking/currency-operations/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Збережений непроведений: зберегти правки без проведення.
  const update = useMutation({
    mutationFn: () =>
      currencyOperations.update(id!, {
        document_date: new Date(form.document_date).toISOString(),
        operation_type: form.operation_type,
        from_bank_account_id: form.from_bank_account_id,
        to_bank_account_id: form.to_bank_account_id,
        currency_id: form.currency_id,
        currency_amount: form.currency_amount,
        commercial_rate: form.commercial_rate,
        nbu_rate: form.nbu_rate,
        bank_commission: form.bank_commission,
        comment: form.comment || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['currency-operation', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await currencyOperations.update(id!, {
        document_date: new Date(form.document_date).toISOString(),
        operation_type: form.operation_type,
        from_bank_account_id: form.from_bank_account_id,
        to_bank_account_id: form.to_bank_account_id,
        currency_id: form.currency_id,
        currency_amount: form.currency_amount,
        commercial_rate: form.commercial_rate,
        nbu_rate: form.nbu_rate,
        bank_commission: form.bank_commission,
        comment: form.comment || null,
      });
      await currencyOperations.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['currency-operation', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const validate = (): boolean => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        from_bank_account_id: form.from_bank_account_id,
        to_bank_account_id: form.to_bank_account_id,
        currency_id: form.currency_id,
      },
      {
        organization_id: 'Юрособа',
        from_bank_account_id: 'З рахунка',
        to_bank_account_id: 'На рахунок',
        currency_id: 'Валюта',
      },
    );
    if (err) { setError(err); return false; }
    if (form.from_bank_account_id === form.to_bank_account_id) {
      setError('Рахунки "З" і "На" мають різнитися');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (isNew) create.mutate();
    else update.mutate();
  };
  const handleSaveAndPost = () => {
    if (!validate()) return;
    createAndPost.mutate();
  };
  const handleUpdateAndPost = () => {
    if (!validate()) return;
    updateAndPost.mutate();
  };

  const isPosted = docQuery.data?.posted ?? false;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова валютна операція' : `Валютна операція ${docQuery.data?.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/banking/currency-operations')}
          onSave={handleSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? handleSaveAndPost : handleUpdateAndPost}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          deletable={false}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover
            withTime
            value={form.document_date}
            disabled={isPosted}
            onChange={(v) => setForm({ ...form, document_date: v })}
          />
        </div>
        <div className="field">
          <RequiredLabel>Тип операції</RequiredLabel>
          <select
            value={form.operation_type}
            disabled={isPosted}
            onChange={(e) => setForm({ ...form, operation_type: e.target.value as CurrencyOperationType })}
          >
            <option value="PURCHASE">Купівля валюти (UAH → валюта)</option>
            <option value="SALE">Продаж валюти (валюта → UAH)</option>
          </select>
        </div>
        <div className="field">
          <RequiredLabel>Валюта (UUID)</RequiredLabel>
          <input
            value={form.currency_id}
            disabled={isPosted}
            onChange={(e) => setForm({ ...form, currency_id: e.target.value })}
          />
        </div>
        <div className="field">
          <RequiredLabel>З рахунка (UUID)</RequiredLabel>
          <input
            value={form.from_bank_account_id}
            disabled={isPosted}
            onChange={(e) => setForm({ ...form, from_bank_account_id: e.target.value })}
          />
        </div>
        <div className="field">
          <RequiredLabel>На рахунок (UUID)</RequiredLabel>
          <input
            value={form.to_bank_account_id}
            disabled={isPosted}
            onChange={(e) => setForm({ ...form, to_bank_account_id: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Сума у валюті</label>
          <input type="number" step="0.0001"
            value={form.currency_amount} disabled={isPosted}
            onChange={(e) => setForm({ ...form, currency_amount: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Курс комерційний</label>
          <input type="number" step="0.00000001"
            value={form.commercial_rate} disabled={isPosted}
            onChange={(e) => setForm({ ...form, commercial_rate: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Курс НБУ</label>
          <input type="number" step="0.00000001"
            value={form.nbu_rate} disabled={isPosted}
            onChange={(e) => setForm({ ...form, nbu_rate: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Комісія банку (UAH)</label>
          <input type="number" step="0.0001"
            value={form.bank_commission} disabled={isPosted}
            onChange={(e) => setForm({ ...form, bank_commission: e.target.value })}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment} disabled={isPosted}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {/* Live FX preview */}
      <div style={{
        marginTop: '1rem', padding: '0.75rem',
        background: '#f0fdf4', borderRadius: 6,
      }}>
        <strong>Розрахунок FX:</strong>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginTop: '0.5rem' }}>
          <div>UAH (комерц): <b>{fxPreview.uahC.toFixed(2)}</b></div>
          <div>UAH (НБУ): <b>{fxPreview.uahN.toFixed(2)}</b></div>
          <div style={{ color: fxPreview.diff < 0 ? '#15803d' : '#b91c1c' }}>
            FX різниця: <b>{fxPreview.diff.toFixed(2)}</b>
            <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}>
              ({fxPreview.diff < 0 ? 'дохід' : 'витрати'})
            </span>
          </div>
        </div>
        {fxPreview.warn && (
          <div style={{ marginTop: '0.5rem', color: '#b45309' }}>⚠ {fxPreview.warn}</div>
        )}
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
